import { FC, useEffect, useRef } from 'react';

import anime from 'animejs';

import styles from './Hero.module.css';
import { animations, attachAnimations, interactiveStyle } from '../../utils/interactions';
import Decorations from '../Decorations/Decorations';
import Icon from '../Icon/Icon';
import ScrollPrompt from '../ScrollPrompt/ScrollPrompt';

const Hero: FC = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const taglineRef = useRef<HTMLDivElement>(null);
  const deviceIconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Initial entrance animation
    const timeline = anime.timeline({
      easing: 'easeOutExpo',
    });

    timeline
      .add({
        targets: titleRef.current,
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 1200,
        delay: 300,
      })
      .add(
        {
          targets: taglineRef.current,
          opacity: [0, 1],
          translateY: [10, 0],
          duration: 800,
        },
        '-=600'
      );

    // Interactive animations
    const cleanup = attachAnimations([
      {
        element: deviceIconRef.current,
        animation: animations.rotate360(deviceIconRef.current!),
        triggers: ['hover', 'touch'],
      },
      {
        element: titleRef.current,
        animation: animations.gentleBounce(titleRef.current!),
        triggers: ['click', 'touch'],
      },
      {
        element: taglineRef.current,
        animation: animations.rock(taglineRef.current!),
        triggers: ['hover', 'touch'],
      },
    ]);

    return cleanup;
  }, []);

  return (
    <section id="hero" className={styles.hero} aria-labelledby="hero-title">
      <Decorations leftIcon="mdi:android" rightIcon="mdi:apple-ios" />

      <div className={styles.content} ref={containerRef}>
        <div className={styles.logoContainer}>
          <div ref={deviceIconRef} style={interactiveStyle}>
            <Icon
              className={styles.headerIcon}
              icon="mdi:devices-mobile"
              size="xl"
              aria-hidden={true}
            />
          </div>
          <h1 id="hero-title" className={styles.title} ref={titleRef} style={interactiveStyle}>
            <span className={styles.titleLine}>Elegance</span>
            <span className={styles.titleAccent}>in</span>
            <span className={styles.titleLine}>Innovation</span>
          </h1>
          <div className={styles.tagline}>
            <div className={styles.taglineText} ref={taglineRef} style={interactiveStyle}>
              <span>
                <span className={styles.taglineHighlight}>Crafting Mobile Excellence</span>
                <span className={styles.taglineDescription}>
                  Transforming ideas into polished native experiences
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.scrollPromptWrapper}>
        <ScrollPrompt targetId="skills" label="Skills" showIcon={true} />
      </div>
    </section>
  );
};

export default Hero;
